/**
 * The React chat (ADR 0021): Transcript + composer + Fan panel for one fan on
 * one Account. Knows nothing about Workspace; a host can put its own header
 * above it (`renderHeader` gets the loaded fan) and pokes `liveTick` when it
 * learns the chat changed (e.g. a socket event) so the newest page reloads.
 */
import { useCallback, useEffect, useState, type PointerEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../components/FeatherIcon';
import { ChatGallery } from './ChatGallery';
import { Composer, type ComposerReply } from './Composer';
import { fillFromMessage, type ComposerFill } from './copyToSend';
import { crmFetch } from './api';
import { autoTranslateOn, setAutoTranslate } from './composerPrefs';
import { FindBar } from './FindInChat';
import { useChatSearch } from './useChatSearch';
import { useTranslations } from './useTranslations';
import { FanPanel } from './FanPanel';
import { PhoneSheet } from '../chats/phone/PhoneSheet';
import { Transcript } from './Transcript';
import { useFan, type FanData } from './useFan';
import { useThread } from './useThread';
import './chat.css';

const PANEL_KEY = 'om_rc_fan_panel';
const PANEL_MIN = 240;
const PANEL_MAX = 560;
const PANEL_DEFAULT = 300;

function clampPanel(width: number): number {
  return Math.round(Math.max(PANEL_MIN, Math.min(PANEL_MAX, Number.isFinite(width) ? width : PANEL_DEFAULT)));
}

/** Fan panel width + shown/hidden: a per-viewer convenience, so browser storage. */
function readPanel(): { width: number; hidden: boolean } {
  try {
    const raw = JSON.parse(localStorage.getItem(PANEL_KEY) || '{}') as { width?: number; hidden?: boolean };
    return { width: clampPanel(Number(raw.width ?? PANEL_DEFAULT)), hidden: raw.hidden === true };
  } catch {
    return { width: PANEL_DEFAULT, hidden: false };
  }
}

function writePanel(panel: { width: number; hidden: boolean }) {
  try {
    localStorage.setItem(PANEL_KEY, JSON.stringify(panel));
  } catch {
    /* storage unavailable */
  }
}

/** What a host's header can do to the open chat. */
export interface ChatControls {
  /** Reload the newest messages. */
  refresh: () => void;
  /** The Find in chat bar is open. */
  finding: boolean;
  toggleFind: () => void;
  /** The chat gallery is open. */
  gallery: boolean;
  toggleGallery: () => void;
  /** Phones with a host side column: the slide-over sheet is open. */
  side?: { open: boolean; toggle: () => void };
  /** Fan messages are translated as they come into view. */
  autoTranslate: { on: boolean; toggle: () => void };
}

/** Where a host's side column sits: beside the chat (resizable, hideable) or in the phone sheet. */
export interface SideFrame {
  sheet: boolean;
  /** Phones: inside the bottom Fan sheet (no dock widgets, no editing, the sheet closes itself). */
  phone?: boolean;
  width?: number;
  resizing?: boolean;
  onResizeStart?: (e: PointerEvent) => void;
  /** Desktop: hide the column; the sheet: close it. */
  onHide: () => void;
}

export interface ChatViewProps {
  accountId: string;
  fanId: string;
  /** The model's display name (who the person is writing as). */
  modelName: string;
  /** CRM username of the person signed in ("You" on their sends). */
  myUsername: string;
  /** Bump to reload the newest messages. */
  liveTick?: number;
  aiStatus?: string | null;
  /** Name to show before the profile loads (from the card that opened the chat). */
  fanNameHint?: string | null;
  renderHeader?: (fan: FanData, chat: ChatControls) => ReactNode;
  /** The host's own side column instead of the Fan panel (the Chats page); on phones it opens as a sheet. */
  renderSide?: (fan: FanData, frame: SideFrame) => ReactNode;
  onSent?: () => void;
  /** Phones: the transcript and composer only; a host side column opens as a sheet. */
  compact?: boolean;
  /** May change the chat on OnlyFans beyond sending (likes); a view-only person sees but cannot. */
  canWrite?: boolean;
  /** Drawn just above the composer (phones: the AI strip). */
  renderBeforeComposer?: (fan: FanData) => ReactNode;
}

export function ChatView({ accountId, fanId, modelName, myUsername, liveTick = 0, aiStatus, fanNameHint, renderHeader, renderSide, onSent, compact = false, canWrite = true, renderBeforeComposer }: ChatViewProps) {
  const { t } = useTranslation('chat');
  const thread = useThread(accountId, fanId, myUsername);
  const fan = useFan(accountId, fanId);
  const { refresh } = thread;

  useEffect(() => {
    if (!liveTick) return;
    const timer = window.setTimeout(refresh, 400);
    return () => window.clearTimeout(timer);
  }, [liveTick, refresh]);

  const fanName = fan.profile?.name || fanNameHint || fanId;

  // Find in chat: a host's header opens the bar; while a search is on the Transcript shows only its hits.
  const [finding, setFinding] = useState(false);
  const find = useChatSearch(accountId, fanId, myUsername);
  const { reset: resetFind } = find;
  const closeFind = useCallback(() => {
    setFinding(false);
    resetFind();
  }, [resetFind]);
  useEffect(closeFind, [accountId, fanId, closeFind]);
  const results = find.search.results;

  // Likes set here hold over what the last messages page said (chats.js messageLikeOverrides).
  const [likes, setLikes] = useState<Map<string, boolean>>(new Map());
  useEffect(() => setLikes(new Map()), [accountId, fanId]);
  const like = useCallback(
    async (messageId: string, liked: boolean) => {
      await crmFetch(liked ? '/v1/messages/like' : '/v1/messages/unlike', { accountId, body: { fan_id: fanId, message_id: messageId } });
      setLikes((prev) => new Map(prev).set(messageId, liked));
    },
    [accountId, fanId],
  );
  const withLikes = (list: typeof thread.messages) => (likes.size ? list.map((m) => (likes.has(m.id) ? { ...m, liked: likes.get(m.id) } : m)) : list);

  const tr = useTranslations(`${accountId}:${fanId}`);
  const [autoTranslate, setAuto] = useState(autoTranslateOn);
  const toggleAutoTranslate = () => {
    const on = !autoTranslate;
    setAutoTranslate(on);
    setAuto(on);
    if (!on) tr.dropAuto();
  };

  // The gallery's card jumps to its message in the whole chat.
  const [gallery, setGallery] = useState(false);
  const [jumpTo, setJumpTo] = useState<string | null>(null);
  useEffect(() => {
    setGallery(false);
    setJumpTo(null);
  }, [accountId, fanId]);
  // The message a gallery card jumped to flashes, then settles (chats.js chat-tools-flash).
  useEffect(() => {
    if (!jumpTo) return;
    const timer = window.setTimeout(() => setJumpTo(null), 1600);
    return () => window.clearTimeout(timer);
  }, [jumpTo]);

  // Reply / Copy to send from the Transcript feed the composer; neither follows the person to another chat.
  const [reply, setReply] = useState<ComposerReply | null>(null);
  const [fill, setFill] = useState<{ draft: ComposerFill; n: number } | null>(null);
  useEffect(() => {
    setReply(null);
    setFill(null);
  }, [accountId, fanId]);

  const [sheet, setSheet] = useState(false);
  useEffect(() => setSheet(false), [accountId, fanId, compact]);

  const [panel, setPanel] = useState(readPanel);
  const [dragW, setDragW] = useState<number | null>(null);
  const updatePanel = (next: { width: number; hidden: boolean }) => {
    setPanel(next);
    writePanel(next);
  };
  const startResize = (e: PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const sx = e.clientX;
    const w0 = panel.width;
    let w = w0;
    setDragW(w0);
    const move = (ev: globalThis.PointerEvent) => {
      // The handle sits on the panel's left edge: dragging left widens it.
      w = clampPanel(w0 - (ev.clientX - sx));
      setDragW(w);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setDragW(null);
      updatePanel({ width: w, hidden: false });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div className="rc-chat">
      <div className="rc-main">
        {renderHeader?.(fan, {
          refresh,
          finding,
          toggleFind: () => (finding ? closeFind() : (setFinding(true), setGallery(false))),
          gallery,
          toggleGallery: () => setGallery((v) => !v),
          side: compact && renderSide ? { open: sheet, toggle: () => setSheet((v) => !v) } : undefined,
          autoTranslate: { on: autoTranslate, toggle: toggleAutoTranslate },
        })}
        {gallery && (
          <ChatGallery
            accountId={accountId}
            messages={thread.messages}
            hasOlder={thread.hasOlder}
            loadingOlder={thread.loadingOlder}
            onLoadOlder={thread.loadOlder}
            onClose={() => setGallery(false)}
            onPick={(id) => {
              closeFind();
              setGallery(false);
              setJumpTo(id);
            }}
          />
        )}
        {finding && <FindBar search={find.search} onRun={(q) => void find.run(q)} onStep={find.step} onClose={closeFind} />}
        <Transcript
          accountId={accountId}
          fanId={fanId}
          fanName={fanName}
          messages={withLikes(results ?? thread.messages)}
          loading={results ? false : thread.loading}
          loadingOlder={results ? false : thread.loadingOlder}
          hasOlder={results ? false : thread.hasOlder}
          error={results ? find.search.error : thread.error}
          emptyText={results ? t('find.none') : undefined}
          highlightId={results ? find.currentId : jumpTo}
          onLike={canWrite ? like : undefined}
          translations={tr.translations}
          onTranslate={(m) => tr.translate(m.id, m.text, true)}
          onSeen={autoTranslate ? tr.seen : undefined}
          onLoadOlder={thread.loadOlder}
          onReply={(m) => setReply({ id: m.id, text: m.text })}
          onCopyToSend={canWrite ? (m) => setFill((prev) => ({ draft: fillFromMessage(m), n: (prev?.n ?? 0) + 1 })) : undefined}
          onUnsend={thread.unsend}
          onRetry={thread.retry}
          onDiscard={thread.discard}
          holdToAct={compact}
        />
        {renderBeforeComposer?.(fan)}
        <Composer
          accountId={accountId}
          fanId={fanId}
          modelName={modelName}
          reply={reply}
          onClearReply={() => setReply(null)}
          fill={fill}
          autoTranslate={{ on: autoTranslate, toggle: toggleAutoTranslate }}
          onSend={async (draft, opts) => {
            const result = await thread.send(draft, opts);
            if (result.ok) onSent?.();
            return result;
          }}
        />
      </div>
      {compact ? (
        renderSide && (
          <PhoneSheet open={sheet} onClose={() => setSheet(false)} label={t('fan.info')}>
            {sheet && <div className="ph-fan-sheet">{renderSide(fan, { sheet: true, phone: true, onHide: () => setSheet(false) })}</div>}
          </PhoneSheet>
        )
      ) : panel.hidden ? (
        <button type="button" className="rc-fan-rail" title={t('fan.showPanel')} onClick={() => updatePanel({ ...panel, hidden: false })}>
          <FeatherIcon name="panel-right-open" size={15} />
        </button>
      ) : renderSide ? (
        renderSide(fan, {
          sheet: false,
          width: dragW ?? panel.width,
          resizing: dragW != null,
          onResizeStart: startResize,
          onHide: () => updatePanel({ ...panel, hidden: true }),
        })
      ) : (
        <FanPanel
          accountId={accountId}
          fanId={fanId}
          fan={fan}
          aiStatus={aiStatus}
          width={dragW ?? panel.width}
          resizing={dragW != null}
          onResizeStart={startResize}
          onHide={() => updatePanel({ ...panel, hidden: true })}
        />
      )}
    </div>
  );
}
